import { makeAutoObservable, runInAction } from 'mobx'
import { api, type HistoryFilter } from '@/shared/api/endpoints'
import type {
  Currency, TransactionDetailResponse, TransactionPageResponse, TxStatus, TxType,
} from '@/shared/api/types'
import { Resource } from '@/stores/lib/Resource'

/* Восемь строк на странице — столько нарисовано в макете History.
   Число здесь не косметика: карточка истории сверяется с макетом
   по высоте, и десять строк делали её на 144px выше. */
const PAGE = 8

/**
 * История операций: фильтры, страницы, детали.
 *
 * Фильтры уходят на сервер, а не режут уже загруженную страницу.
 * Резать локально означало бы показывать «ничего не найдено» там, где
 * искомое лежит на третьей странице, — и человек решит, что перевода
 * не было.
 */
export class HistoryStore {
  currency: Currency | null = null
  type: TxType | null = null
  status: TxStatus | null = null
  dateFrom = ''
  dateTo = ''
  search = ''
  offset = 0

  readonly page: Resource<TransactionPageResponse>
  details: TransactionDetailResponse | null = null
  detailsLoading = false

  private searchTimer: number | null = null

  constructor() {
    this.page = new Resource(() => api.money.transactions(this.filter))
    makeAutoObservable<this, 'searchTimer'>(this, { searchTimer: false })
  }

  get filter(): HistoryFilter {
    const filter: HistoryFilter = { limit: PAGE, offset: this.offset }
    if (this.currency) filter['currency'] = this.currency
    if (this.type) filter['type'] = this.type
    if (this.status) filter['status'] = this.status
    if (this.dateFrom) filter['date_from'] = this.dateFrom
    if (this.dateTo) filter['date_to'] = this.dateTo
    if (this.search.trim()) filter['search'] = this.search.trim()
    return filter
  }

  get limit(): number { return PAGE }

  get isFiltered(): boolean {
    return Boolean(this.currency || this.type || this.status
      || this.dateFrom || this.dateTo || this.search.trim())
  }

  load(): void {
    void this.page.load()
  }

  /** Смена любого фильтра сбрасывает страницу: иначе после фильтрации
   *  человек оказывается на седьмой странице списка из двух строк. */
  private refilter(): void {
    this.offset = 0
    void this.page.load()
  }

  setCurrency(value: Currency | null): void { this.currency = value; this.refilter() }
  setType(value: TxType | null): void { this.type = value; this.refilter() }
  setStatus(value: TxStatus | null): void { this.status = value; this.refilter() }
  setDateFrom(value: string): void { this.dateFrom = value; this.refilter() }
  setDateTo(value: string): void { this.dateTo = value; this.refilter() }

  /**
   * Поиск с задержкой: запрос на каждую букву — это два десятка
   * запросов на одну введённую строку и мигающий список под пальцами.
   */
  setSearch(value: string): void {
    this.search = value
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer)
    this.searchTimer = window.setTimeout(() => this.refilter(), 350)
  }

  reset(): void {
    this.currency = null
    this.type = null
    this.status = null
    this.dateFrom = ''
    this.dateTo = ''
    this.search = ''
    this.refilter()
  }

  setOffset(offset: number): void {
    this.offset = offset
    void this.page.load()
    // Наверх: иначе после перехода на страницу человек смотрит
    // в её середину и не понимает, обновилось ли что-нибудь.
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async openDetails(id: string): Promise<void> {
    this.detailsLoading = true
    this.details = null
    try {
      const detail = await api.money.transaction(id)
      runInAction(() => { this.details = detail })
    } finally {
      runInAction(() => { this.detailsLoading = false })
    }
  }

  closeDetails(): void {
    this.details = null
  }
}
